// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useState } from 'react';
import axios from 'axios';
import { AppIcon, Button, IconButton, TextArea, TextInput, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../api/proposalApi';
import type { NeedsObjectivesSectionContent, ProposalSection } from '../../../../types/proposal';
import { useAiBlockDraft } from '../useAiBlockDraft';
import { AiGenerateRow } from './AiGenerateRow';

export type SectionNeedsObjectivesProps = Readonly<{
  proposalId: string;
  /** Anchors the AI drafting session to the relationship, when known. */
  profileId?: string;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  /**
   * Renders the "Client view" preview (mirrors the Breakthrough Report's
   * Advisor view <-> Client view toggle): the bullets and narrative are
   * shown disabled, with no add/remove-bullet controls, no AI-generate row
   * and no Save button. Defaults to false (advisor editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function readContent(section: ProposalSection): NeedsObjectivesSectionContent {
  const content = section.content as NeedsObjectivesSectionContent | null;
  return {
    bullets: content?.bullets ?? [],
    narrative: content?.narrative ?? '',
  };
}

/**
 * NEEDS_OBJECTIVES section (PRD §3.2 row 2): the v1 SoS "Needs & Objectives"
 * narrative bullets, editable manually or drafted with AI (P3 — the
 * SOS_BUILDER assistant replies with `{bullets, narrative?}` which is
 * persisted through the block apply-ai endpoint).
 */
export function SectionNeedsObjectives({
  proposalId,
  profileId,
  section,
  onSaved,
  readOnly = false,
}: SectionNeedsObjectivesProps) {
  const { showToast } = useToast();
  const initial = readContent(section);
  const [bullets, setBullets] = useState<string[]>(initial.bullets);
  const [narrative, setNarrative] = useState(initial.narrative);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleAiApplied = useCallback(
    (updated: ProposalSection) => {
      // The whole block (bullets + narrative) was persisted — replace the
      // local draft with the server's version.
      const next = readContent(updated);
      setBullets(next.bullets);
      setNarrative(next.narrative);
      setDirty(false);
      onSaved(updated);
      showToast('AI draft applied', 'success');
    },
    [onSaved, showToast],
  );

  const ai = useAiBlockDraft({
    proposalId,
    profileId,
    sectionKey: 'NEEDS_OBJECTIVES',
    block: 'needsObjectives',
    buildMessage: (instruction) =>
      `Draft the needs and objectives block for proposal ${proposalId}.${
        instruction ? ` Instruction: ${instruction}` : ''
      }`,
    buildPayload: (parsed) => {
      const parsedBullets = parsed.bullets;
      if (!Array.isArray(parsedBullets) || !parsedBullets.every((b) => typeof b === 'string')) {
        throw new Error('missing bullets');
      }
      return {
        bullets: parsedBullets,
        ...(typeof parsed.narrative === 'string' ? { narrative: parsed.narrative } : {}),
      };
    },
    onApplied: handleAiApplied,
  });

  const handleBulletChange = useCallback((index: number, value: string) => {
    setBullets((prev) => prev.map((b, i) => (i === index ? value : b)));
    setDirty(true);
  }, []);

  const handleAddBullet = useCallback(() => {
    setBullets((prev) => [...prev, '']);
    setDirty(true);
  }, []);

  const handleRemoveBullet = useCallback((index: number) => {
    setBullets((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  }, []);

  const handleNarrativeChange = useCallback((value: string) => {
    setNarrative(value);
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const trimmedBullets = bullets.filter((b) => b.trim().length > 0);
      const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
        content: { bullets: trimmedBullets, narrative },
      });
      setDirty(false);
      onSaved(updated);
      showToast('Needs & Objectives saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save Needs & Objectives'), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, bullets, narrative, proposalId, section.sectionKey, onSaved, showToast]);

  return (
    <div className="pension-builder__section" data-testid="section-needs-objectives">
      {!readOnly && (
        <AiGenerateRow
          idSuffix="needs-objectives"
          instruction={ai.instruction}
          onInstructionChange={ai.setInstruction}
          onGenerate={() => void ai.generate()}
          generating={ai.generating}
          error={ai.error}
        />
      )}
      <div className="pension-builder__bullets" data-testid="section-needs-objectives-bullets">
        {bullets.map((bullet, index) => (
          <div
            // Bullets have no stable id — index key is acceptable for a
            // purely-local, non-reorderable edit list.
            key={index}
            className="pension-builder__bullet-row"
            data-testid={`section-needs-objectives-bullet-row-${index}`}
          >
            <TextInput
              value={bullet}
              onChange={(e) => handleBulletChange(index, e.target.value)}
              placeholder="Needs & objectives bullet…"
              fluid
              disabled={readOnly}
              data-testid={`section-needs-objectives-bullet-input-${index}`}
            />
            {!readOnly && (
              <IconButton
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveBullet(index)}
                icon={<AppIcon name="trash" />}
                aria-label="Remove bullet"
                data-testid={`section-needs-objectives-remove-bullet-${index}`}
              />
            )}
          </div>
        ))}
        {!readOnly && (
          <Button
            variant="outlined"
            size="sm"
            iconLeft={<AppIcon name="add" size={16} />}
            onClick={handleAddBullet}
            data-testid="section-needs-objectives-add-bullet"
          >
            Add bullet
          </Button>
        )}
      </div>

      <TextArea
        label="Narrative"
        value={narrative}
        onChange={(e) => handleNarrativeChange(e.target.value)}
        placeholder="Additional context for the client's needs and objectives…"
        rows={6}
        fluid
        disabled={readOnly || ai.generating}
        data-testid="section-needs-objectives-narrative"
      />

      {!readOnly && (
        <div className="pension-builder__section-footer">
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={saving || !dirty}
            loading={saving}
            data-testid="section-needs-objectives-save-btn"
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

export default SectionNeedsObjectives;
